import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, FlaskConical, Info, Package, Percent } from "lucide-react";
import { toast } from "react-toastify";
import { Card, CardBody, CardHeader, Col, Container, Row } from "reactstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { useLoader } from "../../_helper/LoaderContext";
import { useLab } from "../../_helper/PharmacyProvider";
import {
  formatDisplayDate,
  normalizeDateFormat,
  resolveDoctorDateFormat,
  SUPPORTED_DOCTOR_DATE_FORMATS,
} from "../../utils/dateTime/dateFormat";
import {
  resolveDoctorTimeFormat,
  TIME_FORMAT_12H,
  TIME_FORMAT_24H,
} from "../../utils/dateTime/timeFormat";
import { updateConvertionRate, updateDateTimeFormat, updateTaxConfiguration } from "./service/setting-service";

const SAMPLE_DATE = new Date(2026, 8, 18, 18, 23);

const TIME_OPTIONS = [
  { value: TIME_FORMAT_12H, label: "12 Hours (AM/PM)" },
  { value: TIME_FORMAT_24H, label: "24 Hours" },
];

const TAX_SERVICES = [
  {
    key: "labTests",
    label: "Lab Tests",
    hint: "Individual tests billed on a patient order",
    Icon: FlaskConical,
  },
  {
    key: "packages",
    label: "Test Packages",
    hint: "Bundled lab test packages",
    Icon: Package,
  },
];

const applyToFromApi = (taxAppliedTo) => {
  if (taxAppliedTo === "test") return { labTests: true, packages: false };
  if (taxAppliedTo === "package") return { labTests: false, packages: true };
  if (taxAppliedTo === "both") return { labTests: true, packages: true };
  return null;
};

const toTaxAppliedTo = (selection) => {
  if (selection.labTests && selection.packages) return "both";
  if (selection.labTests) return "test";
  if (selection.packages) return "package";
  return null;
};

const resolveSavedTax = (lab) => {
  if (!lab) return null;
  const nested = lab.taxConfig || lab.taxConfiguration || lab.tax_configuration;
  if (nested && (nested.taxType || nested.taxAppliedTo || nested.enabled !== undefined)) {
    return nested;
  }
  if (lab.taxType || lab.taxAppliedTo) {
    return {
      enabled: lab.taxType !== "none",
      taxType: lab.taxType,
      taxAppliedTo: lab.taxAppliedTo,
    };
  }
  return null;
};

const LabSettings = () => {
  const { t } = useTranslation();
  const { setLoading } = useLoader();
  const { currentLab, setCurrentLab } = useLab();

  const [dateFormat, setDateFormat] = useState("DD/MM/YYYY");
  const [timeFormat, setTimeFormat] = useState(TIME_FORMAT_12H);
  const [savingFormat, setSavingFormat] = useState(false);

  const [conversionRates, setConversionRates] = useState({});

  const [taxEnabled, setTaxEnabled] = useState(true);
  const [taxType, setTaxType] = useState("gst");
  const [applyTo, setApplyTo] = useState({ labTests: true, packages: true });
  const [savingTax, setSavingTax] = useState(false);

  const baseCurrency = currentLab?.baseCurrency || "INR";
  const allowedCurrencies = currentLab?.allowedCurrencies || [];
  const extraCurrencies = allowedCurrencies.filter((currency) => currency !== baseCurrency);

  const dateOptions = useMemo(
    () =>
      SUPPORTED_DOCTOR_DATE_FORMATS.map((format) => ({
        value: format,
        label: `${format} (${formatDisplayDate(SAMPLE_DATE, format)})`,
      })),
    []
  );

  useEffect(() => {
    if (!currentLab) return;

    const resolved = resolveDoctorDateFormat(currentLab);
    const matched = SUPPORTED_DOCTOR_DATE_FORMATS.find(
      (format) => normalizeDateFormat(format) === resolved
    );
    setDateFormat(matched || "DD/MM/YYYY");
    setTimeFormat(resolveDoctorTimeFormat(currentLab));
    setConversionRates(currentLab.conversionRates || {});

    const savedTax = resolveSavedTax(currentLab);
    if (savedTax) {
      const type = savedTax.taxType;
      setTaxEnabled(savedTax.enabled !== false && type !== "none");
      if (type === "gst" || type === "vat") {
        setTaxType(type);
      }
      const mappedApplyTo = applyToFromApi(savedTax.taxAppliedTo);
      if (mappedApplyTo) {
        setApplyTo(mappedApplyTo);
      } else if (savedTax.applyTo) {
        setApplyTo({
          labTests: !!savedTax.applyTo.labTests,
          packages: !!savedTax.applyTo.packages,
        });
      }
    }
  }, [currentLab]);

  const saveFormat = async () => {
    if (!dateFormat || !timeFormat) {
      toast.warning("Date format and time format are required");
      return;
    }

    setSavingFormat(true);
    setLoading(true);
    try {
      const res = await updateDateTimeFormat({
        date_format: dateFormat,
        time_format: timeFormat,
      });

      if (res.data?.success === true) {
        const saved = res.data.data || {};
        const nextUser = {
          ...(currentLab || {}),
          date_format: saved.date_format || dateFormat,
          time_format: saved.time_format || timeFormat,
        };
        localStorage.setItem("currentLab", JSON.stringify(nextUser));
        setCurrentLab(nextUser);
        toast.success(res.data.message || "Date and time format updated successfully.");
      }
    } catch (err) {
      // The request interceptor already shows the API error.
    } finally {
      setSavingFormat(false);
      setLoading(false);
    }
  };

  const handleRateChange = (currency, value) => {
    setConversionRates((prev) => ({
      ...prev,
      [currency]: parseFloat(value) || 0,
    }));
  };

  const saveConversionRates = async () => {
    setLoading(true);
    try {
      const res = await updateConvertionRate({ conversionRates });
      if (res.data.success == true) {
        toast.success(res.data.message);
        const savedLab = res.data.data || {};
        const nextLab = {
          ...(currentLab || {}),
          ...savedLab,
          taxType: savedLab.taxType || currentLab?.taxType,
          taxAppliedTo: savedLab.taxAppliedTo || currentLab?.taxAppliedTo,
          taxConfig: savedLab.taxConfig || currentLab?.taxConfig,
        };
        localStorage.setItem("currentLab", JSON.stringify(nextLab));
        setCurrentLab(nextLab);
      }
    } catch (err) {
      toast.warning(t("Failed to update conversion rates"));
    } finally {
      setLoading(false);
    }
  };

  const toggleService = (key) => {
    setApplyTo((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const saveTax = async () => {
    const taxAppliedTo = toTaxAppliedTo(applyTo);
    if (taxEnabled && !taxAppliedTo) {
      toast.warning("Select at least one lab service to apply tax");
      return;
    }

    const payload = {
      enabled: taxEnabled,
      taxType: taxEnabled ? taxType : "none",
      taxAppliedTo: taxAppliedTo || "both",
    };

    setSavingTax(true);
    setLoading(true);
    try {
      const res = await updateTaxConfiguration(payload);
      if (res.data?.success === true) {
        const saved = res.data.data || payload;
        const nextLab = {
          ...(currentLab || {}),
          taxType: saved.taxType,
          taxAppliedTo: saved.taxAppliedTo,
          taxConfig: saved,
        };
        localStorage.setItem("currentLab", JSON.stringify(nextLab));
        setCurrentLab(nextLab);
        toast.success(res.data.message || "Tax configuration saved successfully.");
      }
    } catch (err) {
      // The request interceptor already shows the API error.
    } finally {
      setSavingTax(false);
      setLoading(false);
    }
  };

  return (
    <>
      <Breadcrumbs mainTitle="Manage Settings" parent="Home" title="Manage Settings" />
      <Container fluid={true} className="lab-settings">
        <Row className="g-3">
          <Col xl="4" lg="6">
            <Card className="border-0 shadow-sm h-100 lab-settings-card">
              <CardHeader className="bg-white border-0 lab-settings-head">
                <h5 className="mb-1 fw-bold lab-settings-title">Date & Time Format</h5>
                <p className="text-muted mb-0 lab-settings-sub">
                  Choose how dates and times appear across lab reports and orders.
                </p>
              </CardHeader>
              <CardBody className="lab-settings-body d-flex flex-column">
                <Row className="g-2">
                  <Col md="12">
                    <label className="form-label fw-semibold" htmlFor="date-format">
                      Date Format
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      id="date-format"
                      className="form-select"
                      value={dateFormat}
                      onChange={(event) => setDateFormat(event.target.value)}
                    >
                      {dateOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Col>
                  <Col md="12">
                    <label className="form-label fw-semibold" htmlFor="time-format">
                      Time Format
                      <span className="text-danger">*</span>
                    </label>
                    <select
                      id="time-format"
                      className="form-select"
                      value={timeFormat}
                      onChange={(event) => setTimeFormat(event.target.value)}
                    >
                      {TIME_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </Col>
                </Row>
                <div className="lab-settings-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={saveFormat}
                    disabled={savingFormat}
                  >
                    Save
                  </button>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" lg="6">
            <Card className="border-0 shadow-sm h-100 lab-settings-card">
              <CardHeader className="bg-white border-0 lab-settings-head">
                <h5 className="mb-1 fw-bold lab-settings-title">{t("Currency Conversion Rates")}</h5>
                <p className="text-muted mb-0 lab-settings-sub">
                  {t("Base Currency")}: <strong>{baseCurrency}</strong>
                </p>
              </CardHeader>
              <CardBody className="lab-settings-body d-flex flex-column">
                {extraCurrencies.length === 0 ? (
                  <p className="text-muted mb-0 lab-settings-sub">No additional currencies are configured for this lab.</p>
                ) : (
                  <Row className="g-2">
                    {extraCurrencies.map((currency) => (
                      <Col md="12" key={currency}>
                        <label className="form-label fw-semibold">{currency}</label>
                        <div className="input-group">
                          <span className="input-group-text">1 {baseCurrency} =</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={conversionRates[currency] ?? ""}
                            onChange={(e) => handleRateChange(currency, e.target.value)}
                            className="form-control"
                          />
                          <span className="input-group-text">{currency}</span>
                        </div>
                      </Col>
                    ))}
                  </Row>
                )}
                <div className="lab-settings-actions">
                  <button type="button" className="btn btn-primary" onClick={saveConversionRates}>
                    {t("Save")}
                  </button>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" lg="6">
            <TaxConfiguration
              enabled={taxEnabled}
              taxType={taxType}
              applyTo={applyTo}
              onToggleEnabled={() => setTaxEnabled((prev) => !prev)}
              onTaxType={setTaxType}
              onToggleService={toggleService}
              onSave={saveTax}
              saving={savingTax}
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};

const TaxConfiguration = ({
  enabled,
  taxType,
  applyTo,
  onToggleEnabled,
  onTaxType,
  onToggleService,
  onSave,
  saving,
}) => (
  <div className="lab-tax-card">
    <div className="d-flex align-items-start justify-content-between gap-2">
      <div className="d-flex align-items-center gap-2">
        <div className="lab-tax-icon">
          <Percent size={15} />
        </div>
        <div>
          <h5 className="lab-tax-title">Tax Configuration</h5>
          <p className="lab-tax-sub">Select tax type, then choose which lab services are taxed.</p>
        </div>
      </div>
      <div className="text-center flex-shrink-0">
        <button
          type="button"
          className={`lab-tax-switch${enabled ? " on" : ""}`}
          role="switch"
          aria-checked={enabled}
          aria-label="Enable tax"
          onClick={onToggleEnabled}
        >
          <span />
        </button>
        <div className="lab-tax-status">{enabled ? "ENABLED" : "DISABLED"}</div>
      </div>
    </div>

    <div className={enabled ? "" : "lab-tax-disabled"}>
      <div className="lab-tax-label">TAX TYPE</div>
      <Row className="g-2">
        <Col sm="6">
          <button
            type="button"
            className={`lab-tax-option${taxType === "gst" ? " active" : ""}`}
            onClick={() => onTaxType("gst")}
          >
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div>
                <div className="name">GST</div>
                <div className="hint">CGST, SGST & IGST</div>
              </div>
              <span className={`lab-tax-badge${taxType === "gst" ? " on" : " off"}`}>
                {taxType === "gst" ? "ON" : "OFF"}
              </span>
            </div>
          </button>
        </Col>
        <Col sm="6">
          <button
            type="button"
            className={`lab-tax-option${taxType === "vat" ? " active" : ""}`}
            onClick={() => onTaxType("vat")}
          >
            <div className="d-flex justify-content-between align-items-start gap-2">
              <div>
                <div className="name">VAT</div>
                <div className="hint">Single VAT rate</div>
              </div>
              <span className={`lab-tax-badge${taxType === "vat" ? " on" : " off"}`}>
                {taxType === "vat" ? "ON" : "OFF"}
              </span>
            </div>
          </button>
        </Col>
      </Row>

      <div className="lab-tax-label">APPLY TAX TO</div>
      <Row className="g-2">
        {TAX_SERVICES.map(({ key, label, hint, Icon }) => {
          const selected = !!applyTo[key];
          return (
            <Col sm="6" key={key}>
              <button
                type="button"
                className={`lab-tax-apply${selected ? " active" : ""}`}
                title={hint}
                onClick={() => onToggleService(key)}
              >
                <span className="lab-tax-apply-icon">
                  <Icon size={13} />
                </span>
                <span className="name d-flex align-items-center gap-1">
                  {label}
                  <Info size={12} color="#9aa3b5" />
                </span>
                <span className={`lab-tax-check${selected ? " on" : ""}`}>
                  {selected ? <Check size={12} strokeWidth={3} /> : null}
                </span>
              </button>
            </Col>
          );
        })}
      </Row>

      <div className="lab-tax-note">
        <Info size={13} color="#5b6b88" style={{ marginTop: 1, flexShrink: 0 }} />
        <span>Tax rates are set per test or package. This controls the tax system and which lab services are taxed.</span>
      </div>
    </div>

    <div className="text-end mt-3">
      <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
        Save
      </button>
    </div>

    <style>{`
      .lab-settings { max-width: 1080px; }
      .lab-settings-card {
        border-radius: 12px;
        display: flex;
        flex-direction: column;
      }
      .lab-settings-head { padding: 12px 14px 0; }
      .lab-settings-card > .card-body.lab-settings-body {
        flex: 1 1 auto;
        display: flex;
        flex-direction: column;
        padding: 12px 14px;
      }
      .lab-settings-actions {
        margin-top: auto;
        text-align: right;
        padding-top: 12px;
      }
      .lab-settings-title { font-size: 15px; line-height: 1.3; }
      .lab-settings-sub { font-size: 12px; line-height: 1.35; }
      .lab-settings .form-label {
        font-size: 12px;
        margin-bottom: 0.2rem;
      }
      .lab-settings .form-select,
      .lab-settings .form-control,
      .lab-settings .input-group-text {
        font-size: 13px;
        padding: 0.25rem 0.55rem;
        min-height: 32px;
        height: 32px;
      }
      .lab-settings .btn {
        font-size: 13px;
        padding: 0.28rem 0.85rem;
        line-height: 1.3;
      }
      .lab-tax-card {
        background: #fff;
        border: 1px solid #e6e8ef;
        border-radius: 12px;
        padding: 12px 14px;
        box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
        height: 100%;
      }
      .lab-tax-icon {
        width: 30px;
        height: 30px;
        border-radius: 8px;
        background: #f3f4f8;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #3d4663;
        flex-shrink: 0;
      }
      .lab-tax-title {
        font-size: 15px;
        font-weight: 700;
        color: #1f2433;
        margin: 0;
        line-height: 1.3;
      }
      .lab-tax-sub {
        color: #8b90a0;
        font-size: 12px;
        margin: 1px 0 0;
        line-height: 1.35;
      }
      .lab-tax-switch {
        width: 36px;
        height: 20px;
        border-radius: 999px;
        border: none;
        background: #d5d8e2;
        position: relative;
        cursor: pointer;
        padding: 0;
      }
      .lab-tax-switch.on { background: #1e2a5a; }
      .lab-tax-switch span {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: #fff;
        transition: left 0.15s ease;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
      }
      .lab-tax-switch.on span { left: 18px; }
      .lab-tax-status {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        color: #1e2a5a;
        margin-top: 3px;
      }
      .lab-tax-label {
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.06em;
        color: #9aa0b0;
        margin: 12px 0 6px;
      }
      .lab-tax-option,
      .lab-tax-apply {
        border: 1px solid #e4e7ee;
        border-radius: 8px;
        background: #fff;
        width: 100%;
        cursor: pointer;
        text-align: left;
      }
      .lab-tax-option { padding: 8px 10px; }
      .lab-tax-option.active,
      .lab-tax-apply.active {
        background: #f4f6fb;
        border-color: #d5dbe8;
      }
      .lab-tax-option .name,
      .lab-tax-apply .name {
        font-weight: 700;
        color: #2a3142;
        font-size: 13px;
        line-height: 1.2;
      }
      .lab-tax-option .hint {
        color: #8b90a0;
        font-size: 11px;
        margin-top: 1px;
        line-height: 1.2;
      }
      .lab-tax-badge {
        font-size: 10px;
        font-weight: 700;
        border-radius: 4px;
        padding: 2px 6px;
        line-height: 1.2;
      }
      .lab-tax-badge.on { background: #1e2a5a; color: #fff; }
      .lab-tax-badge.off { color: #b0b6c4; background: transparent; }
      .lab-tax-apply {
        padding: 7px 8px;
        display: flex;
        align-items: center;
        gap: 6px;
      }
      .lab-tax-apply-icon {
        width: 26px;
        height: 26px;
        border-radius: 6px;
        background: #eef0f5;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #3d4663;
        flex-shrink: 0;
      }
      .lab-tax-check {
        width: 18px;
        height: 18px;
        border-radius: 4px;
        border: 1.5px solid #d5d8e2;
        margin-left: auto;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #fff;
        background: #fff;
        flex-shrink: 0;
      }
      .lab-tax-check.on {
        background: #1e2a5a;
        border-color: #1e2a5a;
      }
      .lab-tax-note {
        background: #f3f7fd;
        border-radius: 8px;
        color: #5c6578;
        font-size: 11px;
        line-height: 1.35;
        padding: 8px 10px;
        display: flex;
        gap: 6px;
        align-items: flex-start;
        margin-top: 10px;
      }
      .lab-tax-disabled { opacity: 0.45; pointer-events: none; }
    `}</style>
  </div>
);

export default LabSettings;
